import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import {
  getJobQuantityStating,
  reviseJobQuantity,
  submitJobQuantity,
  type JobQuantityDetail,
  type JobQuantityRevision,
  type JobQuantityStating,
} from '../../api/jobQuantityApi.js';
import {
  nextBillable as billableOf,
  quantityFieldChanged,
  quantityIsMeasured,
} from '../../../../shared/contracts/job-quantity.js';
import {
  brandingBasisForUom,
  brandingEvidenceKind,
  brandingVerifiableFields,
} from '../../../../shared/contracts/branding-line.js';
import { personLabel } from '../../../../shared/contracts/approval-trail.js';

/**
 * A job's quantity: the figure first stated, the figure standing now, every
 * change made to it with who made it, before and after; and, to whoever holds
 * the open level of the workflow it rides, the boxes to correct it. Used by
 * the quantity screen and by the review screen every reviewer opens from the
 * BO Queue, so the one figure a bill is raised on is shown and corrected one
 * way only.
 *
 * Which boxes appear follows the rate's unit, not a role: area work is measured
 * by height and width, a commercial by its count of spots, goods by their
 * number. A corrected box is tinted so the next reviewer can see at a glance
 * what an earlier level changed, and a change to the billable figure cannot be
 * saved without a reason.
 *
 * When the job has no quantity yet - the field force measured in another unit
 * than its work order is priced in, or measured nothing - whoever the server
 * says may state it is offered the box to state it, in the work order's unit.
 */
const CHANGED_TINT = { backgroundColor: 'var(--color-warning-surface, #fde8e8)' };

export function JobQuantityPanel({
  fetchRow,
  notOpened,
  assignmentId,
}: {
  /** Reads the quantity; null when the job has none yet. */
  readonly fetchRow: () => Promise<JobQuantityDetail | null>;
  /** Shown when the job has no quantity yet. */
  readonly notOpened: string;
  /** The vendor's assignment, so a job with no quantity may have one stated. */
  readonly assignmentId?: string;
}): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { decimal } = useBusinessNumber();

  const [row, setRow] = useState<JobQuantityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [count, setCount] = useState('');
  const [dim1, setDim1] = useState('');
  const [dim2, setDim2] = useState('');
  const [remarks, setRemarks] = useState('');
  const [stating, setStating] = useState<JobQuantityStating | null>(null);
  const [stated, setStated] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRow();
      setRow(data);
      setStating(!data && assignmentId && token ? await getJobQuantityStating(token, assignmentId) : null);
      setStated('');
      setCount(data ? String(data.currentCount ?? '') : '');
      setDim1(data?.currentDim1 == null ? '' : String(data.currentDim1));
      setDim2(data?.currentDim2 == null ? '' : String(data.currentDim2));
      setRemarks('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [fetchRow, assignmentId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p>{t('common.loading')}</p>;
  if (!row) {
    const state = (e: FormEvent) => {
      e.preventDefault();
      if (!token || !assignmentId || !stating?.mayState || !(Number(stated) > 0)) return;
      setBusy(true);
      setError(null);
      void submitJobQuantity(token, { assignmentId, count: Number(stated), countUom: stating.rateUom })
        .then(() => {
          setNotice(t('fa.quantity.saved'));
          return load();
        })
        .catch((err: Error) => setError(err.message))
        .finally(() => setBusy(false));
    };
    return (
      <div aria-label="job-quantity">
        {error ? <p role="alert">{error}</p> : null}
        <p data-testid="quantity-not-opened">{notOpened}</p>
        {stating?.mayState ? (
          <form onSubmit={state} aria-label="state-quantity">
            <label htmlFor="jq-state">{t('fa.quantity.state.count', { unit: unitLabel(stating.rateUom) })}</label>
            <input
              id="jq-state"
              type="number"
              min="0"
              step="any"
              value={stated}
              onChange={(e) => setStated(e.target.value)}
              data-testid="state-count"
            />
            <Button type="submit" variant="primary" disabled={busy || !(Number(stated) > 0)} data-testid="state-quantity">
              {t('fa.quantity.state.save')}
            </Button>
          </form>
        ) : null}
      </div>
    );
  }

  const basis = brandingBasisForUom(row.currentUom);
  const editableFields = brandingVerifiableFields(row.currentUom);
  const frozen = row.finalBillable != null;
  const mayEdit = row.mayRevise && !frozen;
  const measured = quantityIsMeasured(row);

  const nextDim1 = Number(dim1) || null;
  const nextDim2 = Number(dim2) || null;
  const nextCount = Number(count) || 0;
  const nextBillable = billableOf(row, nextCount, nextDim1, nextDim2);

  const billableMoved = nextBillable !== row.currentBillable;
  const needsReason = billableMoved && !remarks.trim();

  const changed = (field: 'count' | 'dim1' | 'dim2') => quantityFieldChanged(row, field);

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!token || needsReason || !mayEdit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    void reviseJobQuantity(token, row.verificationId, {
      count: nextCount,
      countUom: row.currentCountUom,
      dim1: measured ? (nextDim1 ?? undefined) : undefined,
      dim2: measured ? (nextDim2 ?? undefined) : undefined,
      remarks: remarks.trim() || undefined,
    })
      .then(() => {
        setNotice(t('fa.quantity.saved'));
        return load();
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setBusy(false));
  };

  const figure = (v: number | null, uom: string | null) =>
    v == null ? '—' : `${decimal(v)} ${unitLabel(uom ?? '')}`.trim();

  return (
    <div aria-label="job-quantity">
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {frozen ? <p data-testid="frozen-notice">{t('fa.quantity.frozen')}</p> : null}

      <dl>
        <dt>{t('fa.quantity.evidence')}</dt>
        <dd data-testid="evidence-kind">{t(`fa.quantity.evidence.${brandingEvidenceKind(row.currentUom)}`)}</dd>
      </dl>

      <table data-testid="quantity-table">
        <thead>
          <tr>
            <th>{t('fa.quantity.field')}</th>
            <th>{t('fa.quantity.vendorSaid')}</th>
            <th>{t('fa.quantity.currentValue')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('m17.line.quantity')}</td>
            <td data-testid="submitted-count">{row.submittedCount}</td>
            <td>
              <input
                aria-label={t('m17.line.quantity')}
                value={count}
                disabled={!mayEdit || !editableFields.includes('quantity')}
                onChange={(e) => setCount(e.target.value)}
                style={changed('count') ? CHANGED_TINT : undefined}
                data-testid="current-count"
                data-changed={changed('count') ? 'true' : 'false'}
              />
            </td>
          </tr>

          {measured ? (
            <>
              <tr>
                <td>{t('m17.line.height')}</td>
                <td data-testid="submitted-dim1">{row.submittedDim1 ?? '—'}</td>
                <td>
                  <input
                    aria-label={t('m17.line.height')}
                    value={dim1}
                    disabled={!mayEdit}
                    onChange={(e) => setDim1(e.target.value)}
                    style={changed('dim1') ? CHANGED_TINT : undefined}
                    data-testid="current-dim1"
                    data-changed={changed('dim1') ? 'true' : 'false'}
                  />
                </td>
              </tr>
              <tr>
                <td>{t('m17.line.width')}</td>
                <td data-testid="submitted-dim2">{row.submittedDim2 ?? '—'}</td>
                <td>
                  <input
                    aria-label={t('m17.line.width')}
                    value={dim2}
                    disabled={!mayEdit}
                    onChange={(e) => setDim2(e.target.value)}
                    style={changed('dim2') ? CHANGED_TINT : undefined}
                    data-testid="current-dim2"
                    data-changed={changed('dim2') ? 'true' : 'false'}
                  />
                </td>
              </tr>
            </>
          ) : null}

          {basis === 'SLOT' && row.currentDim3 != null ? (
            <tr>
              <td>{t('m17.line.slotLength')}</td>
              <td colSpan={2} data-testid="slot-length">{row.currentDim3}</td>
            </tr>
          ) : null}

          <tr>
            <td>{t('fa.quantity.billable')}</td>
            <td data-testid="submitted-billable">{row.submittedBillable} {unitLabel(row.submittedUom)}</td>
            <td data-testid="next-billable">{nextBillable} {unitLabel(row.currentUom)}</td>
          </tr>
        </tbody>
      </table>

      {mayEdit ? (
        <form onSubmit={save} aria-label="revise-quantity">
          <label htmlFor="jq-remarks">{t('fa.quantity.reason')}</label>
          <input
            id="jq-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            data-testid="revise-remarks"
          />
          {needsReason ? (
            <p role="alert" data-testid="reason-required">{t('fa.quantity.reasonRequired')}</p>
          ) : null}
          <Button type="submit" variant="primary" disabled={busy || needsReason} data-testid="save-revision">
            {t('fa.quantity.save')}
          </Button>
        </form>
      ) : null}

      <h3 style={{ fontSize: 14, margin: '16px 0 6px' }}>{t('fa.quantity.trail')}</h3>
      <DataTable<JobQuantityRevision>
        testId="quantity-trail"
        rowKey={(r) => r.revisionId}
        rowTestId={(r) => `quantity-trail-${r.revisionId}`}
        columns={[
          { key: 'at', header: t('fa.quantity.col.at'), render: (r) => at(r.actedAt) },
          { key: 'by', header: t('fa.quantity.col.by'), render: (r) => personLabel(r.actorName, r.actorCode) ?? '—' },
          { key: 'step', header: t('fa.quantity.col.step'), render: (r) => r.stepLabel ?? '—' },
          { key: 'action', header: t('fa.quantity.col.action'), render: (r) => t(`fa.quantity.action.${r.actionCode}`) },
          { key: 'before', header: t('fa.quantity.col.before'), render: (r) => figure(r.billableBefore, r.billableUom) },
          { key: 'after', header: t('fa.quantity.col.after'), render: (r) => figure(r.billableAfter, r.billableUom) },
          { key: 'remarks', header: t('fa.quantity.col.remarks'), render: (r) => r.remarks ?? '—' },
        ]}
        rows={row.trail}
      />
    </div>
  );
}
